import { useState } from 'react';
import { Plus, History, Wrench, FileCheck2 } from 'lucide-react';
import { MAINT, MAINT_KEYS, maintTags, addMaintTag, maintCheck, maintToggleTag, maintRepair, getRepairer } from '../lib/maint';
import { fill } from '../lib/license';
import { RepairPhoto, RepairThumbs } from './RepairPhotos';
import FlowCheck from './FlowCheck';
import { feat } from '../lib/features';

export default function MaintPanel({ t, obj, onPatch, onReport }) {
  const lang = (t.locale || 'de').slice(0, 2);
  const [tag, setTag] = useState('');
  const [hist, setHist] = useState(false);
  const [rep, setRep] = useState(null);
  const st = obj.maint?.status;
  const fmt = (d) => new Date(d).toLocaleString(t.locale, { dateStyle: 'short', timeStyle: 'short' });
  const add = () => { const v = tag.trim(); if (!v) return; addMaintTag(st, v); onPatch(maintToggleTag(obj, v)); setTag(''); };
  return (
    <div className="maint" data-testid="maint-panel">
      <div className="maint-head"><b>{t.mtTitle}</b>{obj.maint && <small data-testid="maint-last-date">{t.mtLast}: {fmt(obj.maint.date)}</small>}</div>
      <div className="maint-lights">
        {MAINT_KEYS.map((k) => (
          <button key={k} className={`maint-btn mt-${k} ${st === k ? 'on' : ''}`} style={{ '--mt': MAINT[k] }} data-testid={`maint-${k}-btn`} onClick={() => onPatch(maintCheck(obj, k))}>
            <i /> {t['mt_' + k]}
          </button>
        ))}
      </div>
      {(st === 'warn' || st === 'defect') && (
        <div className="maint-tags" data-testid="maint-tags">
          {maintTags(lang, st).map((x, i) => (
            <button key={x} className={`maint-tag ${(obj.maint.tags || []).includes(x) ? 'on' : ''}`} data-testid={`maint-tag-${i}`} onClick={() => onPatch(maintToggleTag(obj, x))}>{x}</button>
          ))}
          <span className="maint-add">
            <input data-testid="maint-tag-input" value={tag} placeholder={t.mtAddTag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            <button className="icon-btn" data-testid="maint-tag-add-btn" onClick={add}><Plus size={14} /></button>
          </span>
        </div>
      )}
      {(st === 'warn' || st === 'defect') && !rep && (
        <button className="btn btn-green btn-sm" data-testid="maint-repair-btn" onClick={() => setRep({ by: getRepairer(), note: '', photos: {} })}><Wrench size={14} /> {t.mtRepairBtn}</button>
      )}
      {rep && (
        <div className="maint-repair" data-testid="maint-repair-form">
          <input data-testid="maint-repair-name-input" placeholder={t.mtRepairBy} value={rep.by} onChange={(e) => setRep({ ...rep, by: e.target.value })} />
          <input data-testid="maint-repair-note-input" placeholder={t.mtRepairNote} value={rep.note} onChange={(e) => setRep({ ...rep, note: e.target.value })} />
          <div className="rep-photos">
            {['before', 'after'].map((w) => <RepairPhoto key={w} t={t} which={w} photo={rep.photos[w]} onChange={(ph) => setRep((r) => ({ ...r, photos: { ...r.photos, [w]: ph || undefined } }))} />)}
          </div>
          {obj.type === 'leak' && (feat('leakCalc') || !feat('gas')) && <FlowCheck t={t} obj={obj} patch={onPatch} />}
          <div className="row">
            <button className="btn btn-outline btn-sm" data-testid="maint-repair-cancel-btn" onClick={() => setRep(null)}>{t.btnCancel}</button>
            <button className="btn btn-green btn-sm" data-testid="maint-repair-save-btn" disabled={!rep.by.trim()} onClick={() => { onPatch(maintRepair(obj, rep.by.trim(), rep.note.trim(), Object.fromEntries(Object.entries(rep.photos).filter(([, v]) => v)))); setRep(null); }}>{t.mtRepairSave}</button>
          </div>
        </div>
      )}
      {obj.maint?.repair && <small className="maint-repaired" data-testid="maint-repaired-info">{fill(t.mtRepairedLine, { by: obj.maint.repair.by, date: fmt(obj.maint.repair.date), prev: t['mt_' + obj.maint.repair.prev] })}{obj.maint.repair.note ? `: ${obj.maint.repair.note}` : ''}</small>}
      {obj.maint?.repair && <RepairThumbs t={t} photos={obj.maint.repair.photos} />}
      {obj.maint?.repair && onReport && <button className="btn btn-share btn-sm" data-testid="maint-repair-report-btn" onClick={() => onReport(obj.maint)}><FileCheck2 size={14} /> {t.rrBtn}</button>}
      {(obj.maintLog || []).length > 0 && (
        <button className="maint-hist-btn" data-testid="maint-history-btn" onClick={() => setHist(!hist)}><History size={12} /> {t.mtHistory} ({obj.maintLog.length})</button>
      )}
      {hist && (
        <ul className="maint-hist" data-testid="maint-history">
          {obj.maintLog.map((m, i) => <li key={m.date + '' + i}><i style={{ background: MAINT[m.status] }} />{fmt(m.date)} · {t['mt_' + m.status]}{m.tags?.length ? ` – ${m.tags.join(', ')}` : ''}{m.repair ? ` – ${fill(t.mtRepairedLine, { by: m.repair.by, date: fmt(m.repair.date), prev: t['mt_' + m.repair.prev] })}` : ''}{m.repair && <RepairThumbs t={t} photos={m.repair.photos} />}{m.repair && onReport && <button className="icon-btn" title={t.rrBtn} data-testid={`maint-history-report-btn-${i}`} onClick={() => onReport(m)}><FileCheck2 size={13} /></button>}</li>)}
        </ul>
      )}
    </div>
  );
}
